import { action, makeObservable, observable } from 'mobx';
import { VoiceLevelMonitor } from '../../services/core/voice-level-monitor';
import { isVoiceLevelLoggingEnabled, isVoiceLevelMeterEnabled } from '../../utils/feature-switches';
import {
  meterSegments,
  meterState,
  SpeakUpTracker,
  summarizeVoiceLevels,
  VOICE_LEVEL,
  VoiceLevelFrame,
  VoiceLevelState,
  VoiceLevelSummary,
} from '../../utils/voice-level';

/**
 * The voice level of one reading: the meter and speak-up bubble on screen,
 * and the numbers for the READING_VOICE_LEVEL log row.
 *
 * Owned by SpeechRecognitionController, which calls resume() when the audio
 * gate opens, pause() when it closes, takeSummary() when a stream ends and
 * resetReading() when a new reading starts. Nothing here touches the audio
 * sent to the server or the scoring.
 */
export class VoiceLevelController {
  /** Shown on screen. Only updated while the meter switch is on. */
  segments = 0;
  state: VoiceLevelState = 'silent';
  speakUpVisible = false;

  readonly meterEnabled: boolean;
  readonly loggingEnabled: boolean;

  private readonly monitor = new VoiceLevelMonitor((frame) => this.handleFrame(frame));
  private readonly tracker = new SpeakUpTracker();
  private frames: VoiceLevelFrame[] = [];
  private promptsThisStream = 0;
  private hideBubbleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
    private readonly now: () => number = () => Date.now(),
  ) {
    this.meterEnabled = isVoiceLevelMeterEnabled(hostname);
    this.loggingEnabled = isVoiceLevelLoggingEnabled(hostname);
    makeObservable<VoiceLevelController, 'handleFrame' | 'hideBubble'>(this, {
      segments: observable,
      state: observable,
      speakUpVisible: observable,
      handleFrame: action,
      hideBubble: action,
      pause: action,
      resetReading: action,
    });
  }

  private get active(): boolean {
    return this.meterEnabled || this.loggingEnabled;
  }

  /** The audio gate opened: start (or keep) measuring this source. */
  resume(audioContext: AudioContext | null, source: AudioNode | null): void {
    if (!this.active || !audioContext || !source) return;
    try {
      this.monitor.start(audioContext, source);
    } catch (error) {
      // A meter must never stop a reading.
      console.warn('Voice level monitor could not start:', error);
    }
  }

  /** Listening stopped (pause, delete, end). Keeps the frames gathered so far. */
  pause(): void {
    this.monitor.stop();
    this.tracker.breakRun();
    this.segments = 0;
    this.state = 'silent';
    this.hideBubble();
  }

  /**
   * The stream is ending: returns the summary for its log row (or null when
   * logging is off or nothing was measured) and starts the next stream empty.
   */
  takeSummary(): VoiceLevelSummary | null {
    const frames = this.frames;
    const prompts = this.promptsThisStream;
    this.frames = [];
    this.promptsThisStream = 0;
    if (!this.loggingEnabled || frames.length === 0) return null;
    return summarizeVoiceLevels(frames, prompts);
  }

  /** A new reading: the speak-up bubble may show once again. */
  resetReading(): void {
    this.pause();
    this.tracker.reset();
    this.frames = [];
    this.promptsThisStream = 0;
  }

  private handleFrame(frame: VoiceLevelFrame): void {
    if (this.loggingEnabled) this.frames.push(frame);
    if (!this.meterEnabled) return;

    // Only write observables when they change, so the meter re-renders at
    // most when a segment or colour does.
    const segments = meterSegments(frame);
    const state = meterState(frame);
    if (segments !== this.segments) this.segments = segments;
    if (state !== this.state) this.state = state;

    if (this.tracker.update(frame, this.now())) {
      this.promptsThisStream += 1;
      this.speakUpVisible = true;
      this.hideBubbleTimer = setTimeout(() => this.hideBubble(), VOICE_LEVEL.SPEAK_UP_VISIBLE_MS);
    }
  }

  private hideBubble(): void {
    if (this.hideBubbleTimer !== null) {
      clearTimeout(this.hideBubbleTimer);
      this.hideBubbleTimer = null;
    }
    this.speakUpVisible = false;
  }
}
